import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useCurrencyFormatter } from '@/common/hooks/useCurrencyFormatter'
import { getSecondaryProcessRows } from '@/common/utils/OrderUtils/OrderUtilities'

/**
 * Indented rows that break an order's "Secondary processes" amount into folding and each
 * secondary process. The rows add up to that amount as it is displayed.
 * @param {Object} props
 * @param {string} props.currencyCode - Store currency, the same one the summary formats with.
 * @param {string} props.locale - Store locale.
 * @param {Object} props.order - The order, for its folding total.
 * @param {Array} props.orderItems - The order items with their secondary processes.
 * @returns {JSX.Element|null} The rows, or nothing when there is nothing to break down.
 */
const SecondaryProcessRows = ({ currencyCode, locale, order, orderItems }) => {
    const { t } = useTranslation()
    const organisationId = useSelector(selectOrganisationId)
    const { data: secondaryProcesses, isLoading } = useGetSecondaryProcessesQuery({ organisationId })
    const { formatCurrency, formatter } = useCurrencyFormatter({ currencyCode, locale })
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits

    const rows = useMemo(
        () => getSecondaryProcessRows({ fractionDigits, order, orderItems, secondaryProcesses }),
        [fractionDigits, order, orderItems, secondaryProcesses]
    )

    // Without the list every process would briefly show as "Other processing"
    if (isLoading || !rows.length) return null

    const fixedLabels = { Folding: t('Folding'), Other: t('Other processing') }

    return (
        <Box
            aria-label={t('Secondary process breakdown')}
            component="ul"
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, listStyle: 'none', m: 0, p: 0, pl: 2 }}
        >
            {rows.map(({ id, name, total }) => (
                <Box
                    component="li"
                    key={id}
                    sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
                >
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {name ?? fixedLabels[id]}
                    </Typography>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {formatCurrency(total)}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}

SecondaryProcessRows.propTypes = {
    currencyCode: PropTypes.string,
    locale: PropTypes.string,
    order: PropTypes.object,
    orderItems: PropTypes.array,
}

export default SecondaryProcessRows
